import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import { withLoading, addDays, sortLockersFirstStable, toDateKey, isLocker } from '../utils';
import { excludeAmazonLockerNature, isAmazonLocker } from '../utils/amazonLocker';

Vue.use(Vuex);

/**
 * Calculer l'indisponibilité réelle du relais à présenter au client final, et ajouter les propriétés utiles au parcelshop.
 * Cette fonction utilise les règles définies des -10 jours (#1112) avant date de début et -3 jours avant date de fin de congés:
 * * parcelshop.isUnavailable est un booléen indiquant si le parcelshop est indisponible;
 * * parcelshop.unavailability: contient les dates de début (from) et date de fin (to) des congés à présenter.
 * @param {Parcelshop} parcelshop Le point relais
 * @param {boolean} enableUnavailability Est-ce que la disponibilité est activée sur les infos Point Relais?
 */
function computeUnavailability(parcelshop, enableUnavailability) {
  if (!parcelshop.Conges) {
    return { ...parcelshop, isUnavailable: false, unavailability: undefined };
  }

  let nowMs = Date.now();

  const periods = parcelshop.Conges.map(period => {
    return {
      from: new Date(period.Debut),
      to: new Date(period.Fin),
    };
  })
    .filter(period => {
      return addDays(period.from, -10).getTime() <= nowMs && nowMs <= period.to.getTime();
    })
    .sort((a, b) => a.to.getTime() - b.to.getTime());

  if (periods.length === 0) {
    return { ...parcelshop, isUnavailable: false, unavailability: false };
  }

  const current = periods[0];
  const limitMs = addDays(current.to, -3).getTime();
  const isUnavailable = !!(enableUnavailability && nowMs <= limitMs);

  return { ...parcelshop, isUnavailable, unavailability: { from: current.from, to: current.to } };
}

let suggestionTimeout = null;
let suggestionAbortController = null;

export default new Vuex.Store({
  state: {
    params: {
      isSelectionModeEnabled: false,
      avoidSaturated: false,
      parcelshopNatureAllowed: '',
      parcelshopAgenceAllowed: '',
    },
    isLoading: false,
    availableCountries: [],
    userSelectedCountry: '',
    parcelshopList: [],
    isSearchInitialized: false,
    focusedParcelshopId: null,
    isPrintInStoreHighlighted: false,
    isCountryPrintInStore: false,
    showFilterPrintInStore: false,
    showFilterLocker: false,
    isLockerHighlighted: false,
    autocomplete: {
      type: '',
      suggestions: [],
    },
    selectedParcelshopId: null,
    fiveStepsTracking: {},
    deliveryCheckpoints: {},
    contextualTracking: '',
    multipleChoices: false,
    choices: [],
    allowedOperations: [],
    allowedReturn: false,
    confirmationStatus: 0,
    expeditionStatus: {},
    finalDestination: {},
    returnDetails: {
      shipment: '',
      url: '',
      postcode: '',
    },
    statusMessages: [],
    nameTheme: 'MR',
    nameLangue: 'FR',
    minLengthNumExpe: 4,
    maxLengthNumExpe: 12,
    doesParameterSet: false,
    isReplacingViewActive: false,
    countryCode: 'FR',
    authorizedNatures: '',
    cmrBigEventActive: true,
    evenementsNonClientChargeur: [],
    destinataireCodePostal: '',
    replaceLockersEnAvant: true,
    codeAgenceLivraison: null,
    entriesUser: {
      shipment: null,
      postcode: null,
      brand: null,
    },
    relayPointsExcluded: [],
    estimatedDeliveryDate: '',
    showEstimatedDeliveryDate: false,
    collectCountry: '',
    deliveryCountry: '',
    deliveryMode: '',
  },
  mutations: {
    setAvailableCountries(state, countries) {
      state.availableCountries = countries;
    },
    setUserSelectedCountry(state, country) {
      state.userSelectedCountry = country;
    },
    setParcelshopList(state, parcelshopList) {
      const list = (parcelshopList ?? []).map(ps => computeUnavailability(ps, true));
      state.parcelshopList = list;
    },
    setIsSearchInitialized(state, isSearchInitialized) {
      state.isSearchInitialized = isSearchInitialized;
    },
    setFocusedParcelshopId(state, focusedParcelshopId) {
      state.focusedParcelshopId = focusedParcelshopId;
    },
    setIsPrintInStoreHighlighted(state, isPrintInStoreHighlighted) {
      state.isPrintInStoreHighlighted = isPrintInStoreHighlighted;
    },
    setIsCountryPrintInStore(state, isCountryPrintInStore) {
      state.isCountryPrintInStore = isCountryPrintInStore;
    },
    setShowFilterPrintInStore(state, showFilterPrintInStore) {
      state.showFilterPrintInStore = showFilterPrintInStore;
    },
    setShowFilterLocker(state, showFilterLocker) {
      state.showFilterLocker = showFilterLocker;
    },
    setIsLockerHighlighted(state, isLockerHighlighted) {
      state.isLockerHighlighted = isLockerHighlighted;
    },
    setAutocomplete(state, { suggestions, type }) {
      state.autocomplete = { suggestions, type };
    },
    resetAutocomplete(state) {
      state.autocomplete = { type: '', suggestions: [] };
    },
    setSelectedParcelshopId(state, id) {
      state.selectedParcelshopId = id;
    },
    setSelectionMode(state, params) {
      state.params.isSelectionModeEnabled = params;
    },
    setSaturatedParams(state, params) {
      state.params.avoidSaturated = params;
    },
    setParcelshopNatureAllowed(state, params) {
      state.params.parcelshopNatureAllowed = params;
    },
    setParcelshopAgenceAllowed(state, params) {
      state.params.parcelshopAgenceAllowed = params;
    },
    setFiveStepsTracking(state, steps) {
      state.fiveStepsTracking = steps;
    },
    setContextualTracking(state, contextualTracking) {
      state.contextualTracking = contextualTracking;
    },
    setDeliveryCheckpoints(state, checkpoints) {
      state.deliveryCheckpoints = checkpoints;
    },
    setTrackingNumberChoices(state, choices) {
      state.choices = choices;
    },
    setMultipleChoices(state, multipleChoices) {
      state.multipleChoices = multipleChoices;
    },
    setAllowedOperations(state, operations) {
      state.allowedOperations = operations;
    },
    setConfirmationStatus(state, confirmationStatus) {
      state.confirmationStatus = confirmationStatus;
    },
    setExpeditionStatus(state, expeditionStatus) {
      state.expeditionStatus = expeditionStatus;
    },
    setFinalDestination(state, finalDestination) {
      state.finalDestination = finalDestination;
    },
    setAllowedReturn(state, allowedReturn) {
      state.allowedReturn = allowedReturn;
    },
    setReturnDetails(state, returnDetails) {
      state.returnDetails = returnDetails;
    },
    setStatusMessages(state, statusMessages) {
      state.statusMessages = statusMessages;
    },
    setLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setTheme(state, nameTheme) {
      state.nameTheme = nameTheme;
    },
    setLangue(state, nameLangue) {
      state.nameLangue = nameLangue;
    },
    setMinLengthNumExpe(state, nb) {
      state.minLengthNumExpe = nb;
    },
    setMaxLengthNumExpe(state, nb) {
      state.maxLengthNumExpe = nb;
    },
    setNaturesReplaceAllowed(state, natures) {
      state.authorizedNatures = natures;
    },
    setCmrBigEventActive(state, cmrActive) {
      state.cmrBigEventActive = cmrActive;
    },
    setEvenementsNonClientChargeur(state, events) {
      state.evenementsNonClientChargeur = events;
    },
    setDestinataireCodePostal(state, codePostal) {
      state.destinataireCodePostal = codePostal;
    },
    setCodeAgenceLivraison(state, codeAgence) {
      state.codeAgenceLivraison = codeAgence;
    },
    setReplaceLockersEnAvant(state, boolLockersEnAvant) {
      state.replaceLockersEnAvant = boolLockersEnAvant;
    },
    setEntriesUser(state, entries) {
      state.entriesUser = entries;
    },
    setRelayPointsExcluded(state, relayPointsExcluded) {
      state.relayPointsExcluded = relayPointsExcluded;
    },
    setReplacingViewActive(state, isActive) {
      state.isReplacingViewActive = isActive;
    },
    setEstimatedDeliveryDate(state, estimatedDeliveryDate) {
      if (!estimatedDeliveryDate || typeof estimatedDeliveryDate !== 'string') return;

      const parts = estimatedDeliveryDate.split('/');
      if (parts.length !== 3) return;

      const [ddStr, mmStr, yyyyStr] = parts;
      const dd = Number(ddStr);
      const mm = Number(mmStr);
      const yyyy = Number(yyyyStr);

      if (!Number.isInteger(dd) || !Number.isInteger(mm) || !Number.isInteger(yyyy)) return;
      if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return;

      const d = new Date(yyyy, mm - 1, dd);
      if (Number.isNaN(d.getTime())) return;

      const onlyDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      if (onlyDate.getTime() < today.getTime()) return;

      const iso = `${onlyDate.getFullYear()}-${String(onlyDate.getMonth() + 1).padStart(
        2,
        '0'
      )}-${String(onlyDate.getDate()).padStart(2, '0')}`;
      state.estimatedDeliveryDate = iso;
    },
    setCollectCountry(state, collectCountry) {
      state.collectCountry = collectCountry;
    },
    setDeliveryCountry(state, deliveryCountry) {
      state.deliveryCountry = deliveryCountry;
    },
    setShowEstimatedDeliveryDate(state, showEstimatedDeliveryDate) {
      state.showEstimatedDeliveryDate = showEstimatedDeliveryDate;
    },
    setDoesParameterSet(state, doesParameterSet) {
      state.doesParameterSet = doesParameterSet;
    },
    setDeliveryMode(state, deliveryMode) {
      state.deliveryMode = deliveryMode;
    },
  },
  actions: {
    async fetchCountries(ctx) {
      return withLoading(ctx, async () => {
        const res = await axios.get('/api/pays');
        const data = Array.isArray(res.data) ? [...res.data] : [];
        ctx.commit('setAvailableCountries', data);
      })();
    },
    async findParcelshops(ctx, { zipCode, city, country }) {
      const params = {
        country: country,
        postcode: zipCode,
        city: city,
        services: '', //FIXME: (#400 Gitlab)
        excludeSat: ctx.state.params.avoidSaturated, // avoid saturated touchpoints
        naturesAllowed: excludeAmazonLockerNature(ctx.state.authorizedNatures, country),
        agencesAllowed: ctx.state.params.parcelshopAgenceAllowed,
        shipment: ctx.state.entriesUser.shipment,
        postcodeShipment: ctx.state.entriesUser.postcode,
        brand: ctx.state.entriesUser.brand,
      };

      const res = await axios.get('/api/parcelshop', { params });
      // Amazon lockers are never offered when replacing a parcelshop
      const data = (Array.isArray(res.data) ? res.data : []).filter(ps => !isAmazonLocker(ps));

      const needsLockerBoost =
        ctx.state.authorizedNatures.includes('C') && ctx.state.replaceLockersEnAvant;

      const sorted = needsLockerBoost ? sortLockersFirstStable(data) : data;

      const list = sorted.filter(ps => {
        const key = `${ps?.Adresse?.Pays?.Code}-${ps?.Numero}`;
        return !ctx.state.relayPointsExcluded.includes(key);
      });

      ctx.commit('setParcelshopList', list);
      ctx.commit('setIsSearchInitialized', true);
      ctx.commit('setFocusedParcelshopId', null);
    },
    async findNearestParcelshops(ctx, { latitude, longitude, country }) {
      const params = {
        country: country,
        postcode: '',
        services: '', //FIXME: Paramètre sensé être facultatif mais pas pour le moment (#400)
        excludeSat: ctx.state.params.avoidSaturated, // Eviter les points relais saturés
        naturesAllowed: excludeAmazonLockerNature(ctx.state.authorizedNatures, country),
        agencesAllowed: ctx.state.params.parcelshopAgenceAllowed,
        latitude,
        longitude,
        shipment: ctx.state.entriesUser.shipment,
        postcodeShipment: ctx.state.entriesUser.postcode,
        brand: ctx.state.entriesUser.brand,
      };

      const res = await axios.get('/api/parcelshop', { params });
      // Amazon lockers are never offered when replacing a parcelshop
      const data = (Array.isArray(res.data) ? res.data : []).filter(ps => !isAmazonLocker(ps));

      const needsLockerBoost =
        ctx.state.authorizedNatures.includes('C') && ctx.state.replaceLockersEnAvant;

      const sorted = needsLockerBoost ? sortLockersFirstStable(data) : data;

      const list = sorted.filter(ps => {
        const key = `${ps?.Adresse?.Pays?.Code}-${ps?.Numero}`;
        return !ctx.state.relayPointsExcluded.includes(key);
      });

      ctx.commit('setParcelshopList', list);
      ctx.commit('setIsSearchInitialized', true);
    },
    focusParcelshop(ctx, { parcelshopId }) {
      ctx.commit('setFocusedParcelshopId', parcelshopId);
    },
    resetTrackingSatuts(ctx) {
      ctx.commit('setTrackingNumberChoices', []);
      ctx.commit('setContextualTracking', '');
      ctx.commit('setFiveStepsTracking', {});
      ctx.commit('setDeliveryCheckpoints', {});
      ctx.commit('setAllowedOperations', {});
      ctx.commit('setAllowedReturn', false);
    },
    async searchForSuggestions(ctx, { type, value }) {
      if (!value) {
        ctx.commit('setAutocomplete', { suggestions: [], type: '' });
        return;
      }

      clearTimeout(suggestionTimeout);
      suggestionTimeout = setTimeout(async () => {
        if (suggestionAbortController) {
          suggestionAbortController.abort();
        }

        suggestionAbortController = new AbortController();

        return withLoading(ctx, async () => {
          const mode = type !== 'mobile' ? (type === 'city' ? 'ville' : 'codePostal') : '';
          const params = { pays: ctx.state.userSelectedCountry, mode, term: value };

          const res = await axios.get('/_mvc/fr-FR/Ville/AutocompleteVilleSuggestions', { params });
          const data = Array.isArray(res.data) ? [...res.data] : [];

          ctx.commit('setAutocomplete', { suggestions: data, type });
        })();
      }, 300);
    },
    async checkDeliveryStatus(ctx, { shipment, postcode, brand, codePays }) {
      return withLoading(ctx, async () => {
        ctx.dispatch('resetTrackingSatuts');

        ctx.commit('setEntriesUser', { shipment, postcode, brand });

        let res = await axios.get('/api/tracking', {
          params: {
            shipment,
            postcode: postcode || '',
            brand,
            codePays,
          },
        });
        const data = { ...res.data };

        if (data.Expedition) {
          ctx.commit('setFiveStepsTracking', data.Expedition.SuiviParEtapes);
        }

        const hasChoices = Array.isArray(data.FiltresRecherche) && data.FiltresRecherche.length > 1;
        ctx.commit('setMultipleChoices', hasChoices);

        if (hasChoices) {
          ctx.commit('setTrackingNumberChoices', [...data.FiltresRecherche]);
          return data;
        }

        if (data.Expedition) {
          ctx.commit('setCollectCountry', data.Expedition.CollectCountryParcel);
          ctx.commit('setDeliveryCountry', data.Expedition.DeliveryCountryParcel);
          ctx.commit('setDeliveryMode', data.Expedition.DeliveryMode);

          ctx.commit('setExpeditionStatus', {
            expeditionNumber: data.Expedition.Numero,
            brand: data.Expedition.Enseigne,
          });

          if (data.Expedition.NaturesPointRelaisCompatibles) {
            let natures = '';
            data.Expedition.NaturesPointRelaisCompatibles.forEach(n => (natures += n + ','));

            // if (shouldForceOnlyLockersFrToFr(natures, ctx.state)) {
            //   ctx.commit('setNaturesReplaceAllowed', 'C');
            // } else {
            ctx.commit('setNaturesReplaceAllowed', natures);
            //}
          }

          const rawISO = data.Expedition.EstimatedDeliveryDate;
          if (rawISO && rawISO !== '0001-01-01T00:00:00') {
            const d = new Date(rawISO);
            if (!Number.isNaN(d.getTime())) {
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              ctx.commit('setEstimatedDeliveryDate', `${dd}/${mm}/${yyyy}`);
            }
          }

          ctx.commit('setContextualTracking', data.Expedition.SuiviContextuel);
          ctx.commit('setFiveStepsTracking', data.Expedition.SuiviParEtapes);
          ctx.commit('setDeliveryCheckpoints', data.Expedition.Evenements);
          ctx.commit('setEvenementsNonClientChargeur', data.Expedition.EvenementsNonClientChargeur);
          ctx.commit('setAllowedReturn', data.Expedition.EstRetourAutorise);

          if (data.Expedition.Replace != null) {
            ctx.commit('setAllowedOperations', data.Expedition.Replace.options);
            ctx.commit('setFinalDestination', data.Expedition.Replace.parcelshop);
            ctx.commit('setDestinataireCodePostal', data.Expedition.DestinataireCodePostal);
            ctx.commit('setCodeAgenceLivraison', data.Expedition.CodeAgenceLivraison);
          }

          return data;
        }
      })();
    },
    async confirmReplacing(ctx, { replacingOption, parcelshopId }) {
      return withLoading(ctx, async () => {
        const res = await axios.post('/api/shipment/delivery-change', {
          CodeMarqueExpedition: ctx.state.expeditionStatus.brand.slice(0, 2),
          NumeroExpeditionSur8: ctx.state.expeditionStatus.expeditionNumber,
          Type: replacingOption,
          PointRelais: parcelshopId ? parcelshopId : '',
        });
        const data = { ...res.data };

        if (data) {
          if (replacingOption === 1) ctx.commit('setConfirmationStatus', 1);
          else if (replacingOption === 2) ctx.commit('setConfirmationStatus', 2);
        } else {
          ctx.commit('setConfirmationStatus', 3);
        }
      })();
    },
    async requestReturn(ctx, { email }) {
      return withLoading(ctx, async () => {
        let res = await axios.post('/api/shipment/return', {
          CodeEnseigne: ctx.state.expeditionStatus.brand,
          NumExpedition: ctx.state.expeditionStatus.expeditionNumber,
          Email: email,
        });
        const data = { ...res.data };

        ctx.commit('setReturnDetails', {
          shipment: data.NumeroExpedition,
          url: data.UrlEtiquette,
          postcode: data.CodePostal,
          city: data.Ville,
        });
      })();
    },
  },
  getters: {
    parcelshopList: state => {
      if (state.isPrintInStoreHighlighted) {
        return [...state.parcelshopList].sort((a, b) => {
          return (
            b.Services.some(e => e.Code === 'PRINT') - a.Services.some(e => e.Code === 'PRINT')
          );
        });
      }

      if (state.showFilterLocker && state.isLockerHighlighted) {
        return [...state.parcelshopList].sort((a, b) => {
          return (isLocker(b) ? 1 : -1) - (isLocker(a) ? 1 : -1);
        });
      }

      return state.parcelshopList;
    },
    getParcelshopById: state => id => {
      return state.parcelshopList.find(parcelshop => parcelshop.id === id);
    },
    getCheckpointsGroupedByDates: state => {
      const byDate = {};

      Object.keys(state.deliveryCheckpoints).forEach(key => {
        const cp = state.deliveryCheckpoints[key];
        const k = toDateKey(cp?.Date);
        if (!k) return;
        (byDate[k] ||= []).push(cp);
      });

      const res = Object.keys(byDate)
        .sort((a, b) => new Date(b) - new Date(a))
        .map(k => byDate[k]);

      return res;
    },
  },
});
